import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, LotusDivider } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { TextLink } from "../components/Bits";
import InterestForm from "../components/InterestForm";
import { ROUTES, breadcrumbs } from "../lib/seo";
import { SITE_URL } from "../lib/constants";
import { OG_SHOW_IMAGE } from "../lib/shows";
import { getEdition, editionJsonLd } from "../lib/editions";

/**
 * Bhajan jamming in Chandigarh — the city page, like /delhi/ and /gurgaon/.
 * Antara's first Chandigarh edition (Bhaav Sangam, Tagore Theatre) sold out;
 * the page records it and asks who wants the next one.
 */
const ED = getEdition("25-october-2026");

export default function Chandigarh() {
  const recapPath = `${ROUTES.editions.path}${ED.slug}/`;
  return (
    <>
      <Seo route={ROUTES.chandigarh} image={OG_SHOW_IMAGE.url} />
      <JsonLd data={[...editionJsonLd(ED, `${SITE_URL}${recapPath}`), breadcrumbs([{ name: ROUTES.chandigarh.crumb, path: ROUTES.chandigarh.path }])]} />

      <PageHero
        eyebrow="Bhajan jamming in Chandigarh"
        title={<>Antara in <span className="gold-grad italic">Chandigarh</span></>}
        intro={`Antara's first Chandigarh evening — ${ED.title}, at ${ED.venue} on ${ED.dateLabel} — sold out ahead of the night. If you've never been inside a bhajan jamming evening, `}
      >
        <p className="mt-3 font-body text-base md:text-lg" style={{ color: "#E8D9C5" }}>
          start with <TextLink to={ROUTES.pillar.path}><span style={{ color: "#E5C880" }}>what bhajan jamming actually is</span></TextLink>.
        </p>
      </PageHero>

      <section className="bg-cream py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <Eyebrow>The first Chandigarh edition</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight text-ink">{ED.title} at Tagore Theatre</h2>
            <p className="mt-4 flex flex-wrap items-center gap-3 font-body text-sm text-ink-2/85">
              <span className="inline-flex items-center gap-2"><Calendar size={15} className="text-gold" /> {ED.dateLabel}</span>
              <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-gold" /> {ED.venue}, Chandigarh</span>
              <span className="rounded-full px-3 py-0.5 text-[11px] font-semibold tracking-[0.14em] uppercase sold-out-badge">Sold out</span>
            </p>
            <p className="mt-6 font-body text-base md:text-lg text-ink-2/90 leading-relaxed">
              Presented with Catapultt Events, {ED.title} brought Anshu Goyal and the Antara band to
              Chandigarh's Tagore Theatre — a live band holding the groove while the whole hall sings
              the refrains. Bhajan jamming is participatory devotional music: not a satsang, not a
              concert, but a room that sings together.
            </p>
            <p className="mt-5 font-body text-base md:text-lg text-ink-2/90 leading-relaxed">
              Read the <Link to={recapPath} className="text-amber underline underline-offset-4">recap of the night</Link>, see
              the <TextLink to={ROUTES.editions.path}>other editions</TextLink>, or browse
              the <TextLink to={ROUTES.gallery.path}>gallery</TextLink>.
            </p>
            <Link
              to={ROUTES.archiveChandigarh.path}
              className="mt-8 flex items-center justify-between gap-4 rounded-2xl border border-gold/30 bg-ivory px-5 py-4 transition hover:border-gold"
              data-testid="chandigarh-archive"
            >
              <span className="font-body text-ink">
                <span className="block font-display text-xl md:text-2xl">Were you there? Find yourself.</span>
                <span className="text-sm text-ink-2/80">336 more photos from the evening, free to download.</span>
              </span>
              <ArrowRight size={20} className="shrink-0 text-amber" aria-hidden="true" />
            </Link>
            <LotusDivider />
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0F0A07] py-20 md:py-28 px-6">
        <InterestForm
          tone="dark"
          heading={<>Want Antara back in <span className="gold-grad italic">Chandigarh</span>?</>}
          blurb="Tell us you're in and the interest list hears first when the next Chandigarh date is announced."
        />
      </section>
    </>
  );
}
